import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Typography,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import BarChartIcon from '@mui/icons-material/BarChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import ScrollableLayout from 'src/layouts/ScrollableLayout';
import { db } from '../instant';
import { getGoogleSlidesUrl, getOneLink, sortMaterialsWithCustomAtBottom } from 'src/utils';
import { BACKEND_BASE_URL } from 'src/constants';
import LoadingIndicator from '../components/LoadingIndicator';
import WasteMaterialChart from '../components/WasteMaterialChart';
import { REPORT_DERIVED_DATA_PLACEHOLDERS, DERIVED_DATA_DISPLAY_NAMES, getReportData } from '../reports';
import { queryReportData } from 'src/schema';

export default function ReportInfoScreen() {
    const { reportId } = useParams<{ reportId: string }>();
    const navigate = useNavigate();
    const [isDownloading, setIsDownloading] = useState(false);
    const [derivedData, setDerivedData] = useState<any>(null);
    const [isLoadingDerivedData, setIsLoadingDerivedData] = useState(false);

    // Query the report data
    const { data, isLoading, error } = queryReportData(reportId);

    // Add auth query to get the user's token
    const { user, isLoading: authIsLoading, error: authError } = db.useAuth();

    const report = getOneLink(data?.reports);
    const event = getOneLink(report?.event);
    const template = report?.template;

    // Add materials and categories query with more details
    const { data: itemsData } = db.useQuery({
        wasteMaterials: {
            category: {},
            $: { order: { order: 'asc' } },
        },
        wasteCategories: {
            $: { order: { order: 'asc' } },
        },
    });

    const allMaterials = itemsData?.wasteMaterials || [];
    const allCategories = itemsData?.wasteCategories || [];

    // Sort materials - custom materials at the bottom within each category
    const sortedMaterials = sortMaterialsWithCustomAtBottom(allMaterials);

    // Group materials by category
    const materialsByCategory = sortedMaterials.reduce((acc: Record<string, typeof allMaterials>, material) => {
        const categoryId = material.category?.id || 'uncategorized';
        if (!acc[categoryId]) {
            acc[categoryId] = [];
        }
        acc[categoryId].push(material);
        return acc;
    }, {});

    // Add state for accordion expansion
    const [expandedSection, setExpandedSection] = useState<string | false>(false);

    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedSection(isExpanded ? panel : false);
    };

    // Load derived data when report is available
    useEffect(() => {
        const loadDerivedData = async () => {
            if (report && event && template?.id) {
                setIsLoadingDerivedData(true);
                try {
                    const params = {
                        materialBreakdown: report.materialBreakdown,
                        materialIds: report.materials?.map(m => m.id) || [],
                        categoryIds: report.categories?.map(c => c.id) || [],
                        datasetIds: report.datasets?.map(d => d.id) || [],
                        templateId: template.id,
                    };

                    const result = await getReportData(template.id, event.id, params);
                    console.log('report data', result);
                    setDerivedData(result.derivedData);
                } catch (error) {
                    console.error('Error loading derived data:', error);
                } finally {
                    setIsLoadingDerivedData(false);
                }
            }
        };

        loadDerivedData();
    }, [report, event, template]);

    // Memoize the arrays to prevent unnecessary re-renders
    const itemIds = useMemo(() => {
        if (!report) return [];
        return report.materialBreakdown ? report.materials?.map(m => m.id) || [] : report.categories?.map(c => c.id) || [];
    }, [report]);

    const datasetIds = useMemo(() => {
        if (!report) return [];
        return report.datasets?.map(d => d.id) || [];
    }, [report]);

    if (isLoading || authIsLoading) {
        return (
            <Box display="flex" justifyContent="center" padding={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || authError || !reportId || !report) {
        return <Typography color="error">Error loading report data</Typography>;
    }

    const handleBack = () => {
        if (event) {
            navigate(`/event/${event.id}/reports`);
        } else {
            navigate('/');
        }
    };

    const openInGoogleSlides = () => {
        if (report.reportGoogleSlidesId) {
            const url = getGoogleSlidesUrl(report.reportGoogleSlidesId);
            window.open(url, '_blank');
        } else {
            alert('Google Slides link is not available');
        }
    };

    const openInGoogleSheets = () => {
        if (report.chartsGoogleSheetsId) {
            const url = `https://docs.google.com/spreadsheets/d/${report.chartsGoogleSheetsId}/edit`;
            window.open(url, '_blank');
        } else {
            alert('Google Sheets charts are not available');
        }
    };

    const handleDownload = async (format: 'png' | 'pdf') => {
        if (!report.reportGoogleSlidesId) {
            alert('Report is not available for download');
            return;
        }

        // Get the token from the authenticated user
        const token = user?.refresh_token;
        if (!token) {
            alert('You must be logged in to download reports');
            return;
        }

        setIsDownloading(true);

        try {
            // Use fetch with blob response type
            const response = await fetch(`${BACKEND_BASE_URL}/export-report`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    token: token, // Add the token to the request headers
                },
                body: JSON.stringify({
                    googleSlidesId: report.reportGoogleSlidesId,
                    format,
                }),
            });

            if (!response.ok) {
                // Try to parse error if it's JSON
                try {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to export report');
                } catch {
                    throw new Error(`Failed to export report: ${response.statusText}`);
                }
            }

            // Get the response as a blob
            const blob = await response.blob();

            // Create a filename
            const fileName = `report-${new Date().toISOString().split('T')[0]}.${format}`;

            // Create download link and trigger download
            const downloadLink = document.createElement('a');
            downloadLink.href = URL.createObjectURL(blob);
            downloadLink.download = fileName;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        } catch (error) {
            console.error('Error downloading report:', error);
            alert(error instanceof Error ? error.message : 'Failed to download report');
        } finally {
            setIsDownloading(false);
        }
    };

    const downloadPdf = () => handleDownload('pdf');
    const downloadImage = () => handleDownload('png');

    console.log('report', report);

    return (
        <ScrollableLayout>
            {isDownloading && <LoadingIndicator message="Downloading report..." />}
            <Stack direction="column" spacing={2} sx={{ my: 4, mx: 0, width: '100%', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <IconButton onClick={handleBack} sx={{ mr: 2 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4">Report Details</Typography>
                </Box>

                <Paper
                    sx={{
                        p: { xs: 2, sm: 3 }, // Responsive padding - 16px on mobile, 24px on larger screens
                        mb: 4,
                        minWidth: '300px',
                        width: '100%',
                        maxWidth: '400px',
                        margin: '20px',
                        boxSizing: 'border-box', // Ensure padding is included in width calculation
                    }}
                >
                    <Typography variant="h6" gutterBottom sx={{ ml: { xs: 0, sm: 1 } }}>
                        {event?.name || 'Unknown Event'}
                    </Typography>

                    <Typography variant="body1" gutterBottom>
                        Created: {report.createdAt ? new Date(report.createdAt).toLocaleString() : 'Unknown date'}
                    </Typography>

                    <Typography variant="body1" gutterBottom>
                        Template: {template?.name || 'Custom Template'}
                    </Typography>

                    <Typography variant="body1" gutterBottom>
                        Waste Breakdown Type: {report.materialBreakdown ? 'By material' : 'By category'}
                    </Typography>

                    <Box sx={{ mt: 2, mb: 3 }}>
                        <Accordion expanded={expandedSection === 'materials'} onChange={handleAccordionChange('materials')} sx={{ mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="materials-content" id="materials-header">
                                <Typography variant="subtitle1" fontWeight="medium">
                                    {report.materialBreakdown
                                        ? `Materials included (${report.materials?.length || 'All'})`
                                        : `Categories included (${report.categories?.length || 'All'})`}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {report.materialBreakdown ? (
                                    <>
                                        {Object.entries(materialsByCategory).map(([categoryId, materials]) => {
                                            const category = allCategories.find(c => c.id === categoryId);
                                            // Skip categories with no selected materials
                                            const hasSelectedMaterials = materials.some(
                                                m => !report.materials?.length || report.materials.some(rm => rm.id === m.id)
                                            );

                                            if (!hasSelectedMaterials && report.materials?.length) return null;

                                            return (
                                                <Box key={categoryId} sx={{ mb: 2 }}>
                                                    {category && (
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1 }}>
                                                            {category.name}
                                                        </Typography>
                                                    )}
                                                    <List dense>
                                                        {materials.map(material => {
                                                            const isIncluded =
                                                                !report.materials?.length ||
                                                                report.materials.some(m => m.id === material.id);
                                                            return (
                                                                <ListItem key={material.id}>
                                                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                                                        {isIncluded && <CheckIcon color="primary" fontSize="small" />}
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary={material.name}
                                                                        sx={{
                                                                            color: isIncluded ? 'text.primary' : 'text.disabled',
                                                                            textDecoration: isIncluded ? 'none' : 'line-through',
                                                                            '& .MuiTypography-root': {
                                                                                fontStyle: material.custom ? 'italic' : 'normal',
                                                                            },
                                                                        }}
                                                                    />
                                                                </ListItem>
                                                            );
                                                        })}
                                                    </List>
                                                    <Divider sx={{ mt: 1 }} />
                                                </Box>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <List dense>
                                        {allCategories.map(category => {
                                            const isIncluded =
                                                !report.categories?.length || report.categories.some(c => c.id === category.id);
                                            return (
                                                <ListItem key={category.id}>
                                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                                        {isIncluded && <CheckIcon color="primary" fontSize="small" />}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={category.name}
                                                        sx={{
                                                            color: isIncluded ? 'text.primary' : 'text.disabled',
                                                            textDecoration: isIncluded ? 'none' : 'line-through',
                                                        }}
                                                    />
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            expanded={expandedSection === 'datasets'}
                            onChange={handleAccordionChange('datasets')}
                            sx={{ display: report.datasets?.length >= 2 ? 'block' : 'none' }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="datasets-content" id="datasets-header">
                                <Typography variant="subtitle1" fontWeight="medium">
                                    Datasets included ({report.datasets?.length || 'All'})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <List dense>
                                    {(report.datasets || []).map(dataset => (
                                        <ListItem key={dataset.id}>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <CheckIcon color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary={dataset.name || 'Unnamed dataset'} />
                                        </ListItem>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion expanded={expandedSection === 'derivedData'} onChange={handleAccordionChange('derivedData')}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="derivedData-content" id="derivedData-header">
                                <Typography variant="subtitle1" fontWeight="medium">
                                    Calculated Values
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {isLoadingDerivedData ? (
                                    <Box display="flex" justifyContent="center" p={2}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : derivedData ? (
                                    <List dense>
                                        {Object.entries(derivedData).map(([key, value]) => {
                                            const placeholder = REPORT_DERIVED_DATA_PLACEHOLDERS[key];

                                            // Use the custom display name if available, otherwise format the key
                                            const displayName =
                                                DERIVED_DATA_DISPLAY_NAMES[key] || key.replace(/([A-Z])/g, ' $1').toLowerCase();

                                            return (
                                                <ListItem key={key}>
                                                    <ListItemText
                                                        primary={
                                                            <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <Typography component="span">{displayName}:</Typography>
                                                                <Typography component="span" fontWeight="bold">
                                                                    {value as string}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        secondary={placeholder && `Placeholder: ${placeholder}`}
                                                    />
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                ) : (
                                    <Typography color="text.secondary">No derived data available for this report.</Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Box>

                    <Typography variant="h5" sx={{ mt: 4, mb: 2, textAlign: 'center' }}>
                        Charts
                    </Typography>

                    <Box sx={{ height: 300, mb: 4 }}>
                        {report && event && (
                            <WasteMaterialChart
                                eventId={event.id}
                                materialBreakdown={report.materialBreakdown}
                                itemIds={itemIds}
                                datasetIds={datasetIds}
                            />
                        )}
                    </Box>

                    <Typography variant="h5" sx={{ mt: 4, mb: 2, textAlign: 'center' }}>
                        Download Options
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 3 }}>
                        <Button
                            variant="contained"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={downloadPdf}
                            sx={{ minWidth: 200, width: { xs: '100%', sm: '250px' } }} // Responsive width
                        >
                            Download PDF
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<ImageIcon />}
                            onClick={downloadImage}
                            sx={{ minWidth: 200, width: { xs: '100%', sm: '250px' } }} // Responsive width
                        >
                            Download Image
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<SlideshowIcon />}
                            onClick={openInGoogleSlides}
                            disabled={!report.reportGoogleSlidesId}
                            sx={{ minWidth: 200, width: { xs: '100%', sm: '250px' } }} // Responsive width
                        >
                            Open in Google Slides
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<BarChartIcon />}
                            onClick={openInGoogleSheets}
                            disabled={!report.chartsGoogleSheetsId}
                            sx={{ minWidth: 200, width: { xs: '100%', sm: '250px' } }} // Responsive width
                        >
                            Open Charts in Sheets
                        </Button>
                    </Box>
                </Paper>
            </Stack>
        </ScrollableLayout>
    );
}
